import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { Tone } from '@/constants/clinic'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TONE_CLASSES } from '@/components/app/status-badge'

interface StatCardProps {
    label: string
    value: ReactNode
    icon: LucideIcon
    tone?: Tone
    hint?: ReactNode
    href?: string
}

const StatCard = ({ label, value, icon: Icon, tone = 'teal', hint, href }: StatCardProps): ReactNode => {
    const body = (
        <>
            <div className='flex items-start justify-between gap-3'>
                <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-lg', TONE_CLASSES[tone].icon)}>
                    <Icon className='size-5' aria-hidden='true' />
                </span>

                {href && (
                    <ArrowUpRight className='size-4 text-muted-foreground opacity-0 transition-opacity group-hover/stat:opacity-100' aria-hidden='true' />
                )}
            </div>

            <div className='mt-4'>
                <p className='text-sm text-muted-foreground'>
                    {label}
                </p>

                <p className='mt-1 font-heading text-2xl font-semibold tracking-tight text-foreground'>
                    {value}
                </p>

                {hint && (
                    <p className='mt-1 truncate text-xs text-muted-foreground'>
                        {hint}
                    </p>
                )}
            </div>
        </>
    )

    const className = 'group/stat flex flex-col rounded-xl border border-border/80 bg-card p-5 shadow-card transition-all'

    if (href) {
        return (
            <Link
                href={href}
                className={cn(className, 'hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-elevated focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none')}
            >
                {body}
            </Link>
        )
    }

    return <div className={className}>{body}</div>
}

export default StatCard
